import { NgTemplateOutlet } from '@angular/common';
import { ChangeDetectionStrategy, Component, computed, inject, signal } from '@angular/core';

import { IonModal } from '@ionic/angular/standalone';

import { DeviceService } from '@core/device';
import { I18nPipe } from '@core/i18n';

import { ButtonComponent } from '@shared/button';
import { IconComponent } from '@shared/icon';
import {
  buildRows,
  MenuComponent,
  menuGroupTitleCva,
  type MenuItem,
  menuItemCva,
  MenuTriggerDirective,
} from '@shared/menu';
import { SKELETON_ROWS, SkeletonComponent } from '@shared/skeleton';
import { TextfieldComponent } from '@shared/textfield';

import { ShellStore } from '../../shell.store';
import { SEARCH_CLEAR_RECENTS, SEARCH_MAX_LENGTH } from './search.types';
import { focusFirstInput, idleSearchItems, resultSearchItems } from './search.util';

@Component({
  selector: 'ml-search',
  imports: [
    ButtonComponent,
    I18nPipe,
    IconComponent,
    IonModal,
    MenuComponent,
    MenuTriggerDirective,
    NgTemplateOutlet,
    SkeletonComponent,
    TextfieldComponent,
  ],
  host: { '[class]': 'device.isMobileUI() ? "contents" : "block w-full"' },
  templateUrl: './search.component.html',
  changeDetection: ChangeDetectionStrategy.OnPush,
})
export class SearchComponent {
  protected readonly shellStore = inject(ShellStore);
  protected readonly device = inject(DeviceService);

  protected readonly query = signal('');
  protected readonly modalOpen = signal(false);
  protected readonly isIos = this.device.isIos;
  protected readonly searchMaxLength = SEARCH_MAX_LENGTH;
  protected readonly skeletonRows = SKELETON_ROWS;
  protected readonly itemClass = menuItemCva;
  protected readonly titleClass = menuGroupTitleCva();
  protected readonly buildRows = buildRows;

  protected readonly searchMenuPanelClass = computed(() => {
    const width = this.device.isDesktopUI() ? 'w-140' : 'w-(--ngp-menu-trigger-width)';
    const hits = this.shellStore.searchHits();
    const height = this.shellStore.loading()
      ? 'max-h-82'
      : this.showEmpty()
        ? 'h-search-empty'
        : hits === null
          ? 'max-h-100'
          : 'max-h-search-results';

    return `header-search-list header-search-menu rounded-2xl [&_ml-icon]:text-secondary ${width} ${height} max-w-[calc(100vw-2rem)]`;
  });

  protected readonly showEmpty = computed(() => {
    const hits = this.shellStore.searchHits();
    return !this.shellStore.loading() && hits !== null && hits.length === 0;
  });

  protected readonly menuItems = computed<readonly MenuItem[]>(() => {
    if (this.shellStore.loading() || this.showEmpty()) return [];

    const hits = this.shellStore.searchHits();
    if (hits === null) return idleSearchItems(this.shellStore.searchRecents());

    return resultSearchItems(hits);
  });

  protected onQueryChange(value: string): void {
    this.query.set(value);
    this.shellStore.search(value);
  }

  protected onOpenModal(): void {
    this.modalOpen.set(true);
  }

  protected onModalDismiss(): void {
    this.modalOpen.set(false);
    this.query.set('');
    this.shellStore.search('');
  }

  protected closeModal(): void {
    this.modalOpen.set(false);
  }

  protected onSearchPresented(event: Event): void {
    focusFirstInput(event.target instanceof HTMLElement ? event.target : null);
  }

  protected onSelect(value: string): void {
    if (value === SEARCH_CLEAR_RECENTS) {
      this.shellStore.clearSearchRecents();
      return;
    }

    // Decorative until search destinations exist.
    if (this.modalOpen()) this.closeModal();
  }
}
